/**
 * Greybox level markers: gates, wave contact volumes, pedestal position.
 * Presentation only — positions come from level data, not sim logic.
 *
 * Gates: green glow for +N / ×M, red for −N; big readable operator text;
 * paired left/right across the lane so steering picks one.
 *
 * Pedestal mesh + holographic mecha live in `pedestalHologram.ts` (desire
 * object owned by GreyboxRenderer) so they can spin / solidify each frame.
 */
import * as THREE from "three";
import { formatGateOpText, gateOpColor, } from "./gateBreak.js";
function makeGateLabelTexture(text, colorHex, size = 256) {
    const canvas = typeof document !== "undefined"
        ? document.createElement("canvas")
        : null;
    // Headless / tests without DOM: empty texture is fine
    if (!canvas) {
        const data = new Uint8Array([255, 255, 255, 255]);
        const tex = new THREE.DataTexture(data, 1, 1);
        tex.needsUpdate = true;
        return tex;
    }
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (ctx) {
        ctx.clearRect(0, 0, size, size);
        // Dark plate for readability on bright bridge
        ctx.fillStyle = "rgba(15, 23, 42, 0.72)";
        const pad = size * 0.06;
        ctx.beginPath();
        const x = pad;
        const y = size * 0.22;
        const w = size - pad * 2;
        const h = size * 0.56;
        const r = size * 0.1;
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + w, y, x + w, y + h, r);
        ctx.arcTo(x + w, y + h, x, y + h, r);
        ctx.arcTo(x, y + h, x, y, r);
        ctx.arcTo(x, y, x + w, y, r);
        ctx.closePath();
        ctx.fill();
        // Border glow
        ctx.strokeStyle = colorHex;
        ctx.lineWidth = size * 0.035;
        ctx.stroke();
        ctx.font = `bold ${Math.floor(size * 0.42)}px system-ui,Segoe UI,sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.lineWidth = Math.max(5, size * 0.045);
        ctx.strokeStyle = "rgba(0,0,0,0.85)";
        ctx.strokeText(text, size / 2, size / 2);
        ctx.fillStyle = colorHex;
        ctx.fillText(text, size / 2, size / 2);
    }
    const tex = new THREE.CanvasTexture(canvas);
    tex.needsUpdate = true;
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
}
function laneX(lane, halfWidth) {
    const l = lane ?? 0;
    // Side-by-side across the lane so steering picks one (not stacked center).
    if (l < 0)
        return -halfWidth * 0.58;
    if (l > 0)
        return halfWidth * 0.58;
    return 0;
}
/**
 * Build decorative gate portals and wave contact slabs.
 * Pedestal desire object is built separately (createPedestalHologram).
 */
export function createLevelMarkers(input) {
    const group = new THREE.Group();
    group.name = "levelMarkers";
    const halfWidth = input.pathHalfWidth ?? 3.5;
    const waveZ = new Map();
    const gateById = new Map();
    /** Gates sharing a Z (binary pairs) for hide-on-choice. */
    const gatesByZ = new Map();
    const gates = input.gates ?? [];
    for (const gate of gates) {
        const color = gateOpColor(gate.op);
        const isRed = gate.op === "sub";
        const x = laneX(gate.lane, halfWidth);
        const y = 1.05;
        const z = gate.z;
        const root = new THREE.Group();
        root.name = `gate:${gate.id}`;
        root.position.set(x, 0, z);
        // Vertical posts (portal frame)
        const postGeo = new THREE.BoxGeometry(0.28, 2.2, 0.28);
        const beamGeo = new THREE.BoxGeometry(1.55, 0.22, 0.28);
        const frameMat = new THREE.MeshStandardMaterial({
            color,
            emissive: color,
            emissiveIntensity: isRed ? 0.55 : 0.65,
            roughness: 0.4,
            metalness: 0.15,
            flatShading: true,
            transparent: true,
            opacity: 0.95,
        });
        const leftPost = new THREE.Mesh(postGeo, frameMat);
        leftPost.position.set(-0.7, 1.1, 0);
        leftPost.castShadow = true;
        leftPost.name = `gatePostL:${gate.id}`;
        root.add(leftPost);
        const rightPost = new THREE.Mesh(postGeo, frameMat.clone());
        rightPost.position.set(0.7, 1.1, 0);
        rightPost.castShadow = true;
        rightPost.name = `gatePostR:${gate.id}`;
        root.add(rightPost);
        const beam = new THREE.Mesh(beamGeo, frameMat.clone());
        beam.position.set(0, 2.15, 0);
        beam.name = `gateBeam:${gate.id}`;
        root.add(beam);
        // Glow plate (readable color field behind operator text)
        const plateGeo = new THREE.PlaneGeometry(1.35, 1.55);
        const plateMat = new THREE.MeshStandardMaterial({
            color,
            emissive: color,
            emissiveIntensity: isRed ? 0.7 : 0.85,
            roughness: 0.55,
            metalness: 0.05,
            flatShading: true,
            transparent: true,
            opacity: 0.55,
            side: THREE.DoubleSide,
            depthWrite: false,
        });
        const plate = new THREE.Mesh(plateGeo, plateMat);
        plate.position.set(0, 1.05, 0.02);
        plate.name = `gateGlow:${gate.id}`;
        root.add(plate);
        // Soft ground halo so gate reads at distance
        const haloGeo = new THREE.CircleGeometry(0.85, 16);
        const haloMat = new THREE.MeshBasicMaterial({
            color,
            transparent: true,
            opacity: 0.28,
            depthWrite: false,
            side: THREE.DoubleSide,
        });
        const halo = new THREE.Mesh(haloGeo, haloMat);
        halo.rotation.x = -Math.PI / 2;
        halo.position.set(0, 0.04, 0);
        halo.name = `gateHalo:${gate.id}`;
        root.add(halo);
        // Big readable operator text (+N / ×M / −N)
        const labelText = formatGateOpText(gate.op, gate.k);
        const colorCss = `#${color.toString(16).padStart(6, "0")}`;
        const labelTex = makeGateLabelTexture(labelText, colorCss, 256);
        const labelMat = new THREE.SpriteMaterial({
            map: labelTex,
            transparent: true,
            depthWrite: false,
            opacity: 1,
        });
        const label = new THREE.Sprite(labelMat);
        label.name = `gateLabel:${gate.id}`;
        label.position.set(0, y, 0.18);
        // Large on-screen read (portrait 9:16)
        label.scale.set(2.4, 2.4, 1);
        root.add(label);
        group.add(root);
        const bodyMats = [
            frameMat,
            leftPost.material,
            rightPost.material,
            beam.material,
            plateMat,
        ];
        const visual = {
            def: gate,
            root,
            bodyMats,
            labelMat,
            labelTex,
            x,
            y,
            z,
            color,
            triggered: false,
        };
        gateById.set(gate.id, visual);
        const zList = gatesByZ.get(gate.z) ?? [];
        zList.push(visual);
        gatesByZ.set(gate.z, zList);
    }
    const waves = input.waves ?? [];
    for (const wave of waves) {
        waveZ.set(wave.id, wave.z);
        const half = wave.contactHalfLength ?? 3;
        const isClimax = wave.id.toLowerCase().includes("climax") ||
            wave.id.toLowerCase().includes("horde") ||
            half >= 12;
        // Subtle contact-zone slab only — living bodies come from the horde system
        // (bare-skin zombie instances streaming on the road, not static decorations).
        const mat = new THREE.MeshStandardMaterial({
            color: isClimax ? 0x991b1b : 0x7f1d1d,
            emissive: isClimax ? 0x7f1d1d : 0x450a0a,
            emissiveIntensity: isClimax ? 0.35 : 0.22,
            roughness: 0.9,
            transparent: true,
            opacity: isClimax ? 0.22 : 0.16,
            flatShading: true,
        });
        const geo = new THREE.BoxGeometry(halfWidth * 1.6, 0.08, half * 2);
        const mesh = new THREE.Mesh(geo, mat);
        mesh.name = `wave:${wave.id}`;
        mesh.position.set(0, 0.04, wave.z);
        mesh.receiveShadow = true;
        group.add(mesh);
    }
    // Pedestal id/z consumed by GreyboxRenderer → createPedestalHologram
    // (silver circular platform + rotating hologram mecha). Keep marker group
    // free of static pedestal mesh to avoid double-draw.
    void input.pedestal;
    const hideVisual = (v) => {
        v.triggered = true;
        v.root.visible = false;
        for (const m of v.bodyMats) {
            m.opacity = 0.1;
        }
        v.labelMat.opacity = 0;
    };
    return {
        group,
        getWaveZ(waveId) {
            return waveZ.get(waveId) ?? null;
        },
        getGateWorld(gateId) {
            const v = gateById.get(gateId);
            if (!v)
                return null;
            return {
                id: v.def.id,
                x: v.x,
                y: v.y,
                z: v.z,
                op: v.def.op,
                k: v.def.k,
                color: v.color,
            };
        },
        setTriggeredGate(gateId, triggered) {
            const v = gateById.get(gateId);
            if (!v)
                return;
            if (!triggered) {
                // Restore not used mid-run; keep simple
                v.triggered = false;
                v.root.visible = true;
                return;
            }
            // Hide entire Z-group (unchosen sibling of a binary pair)
            const siblings = gatesByZ.get(v.def.z) ?? [v];
            for (const s of siblings) {
                hideVisual(s);
            }
        },
        dispose() {
            for (const v of gateById.values()) {
                v.labelTex.dispose();
                v.labelMat.dispose();
            }
            group.traverse((obj) => {
                if (obj instanceof THREE.Mesh) {
                    obj.geometry.dispose();
                    const m = obj.material;
                    if (Array.isArray(m))
                        m.forEach((x) => x.dispose());
                    else
                        m.dispose();
                }
            });
            group.clear();
            gateById.clear();
            gatesByZ.clear();
        },
    };
}
//# sourceMappingURL=levelMarkers.js.map